import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import { sessionTracker } from './sessionTracker';
import { HOTEL_ID } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';

export interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  event_data?: Record<string, any>;
  page_path?: string;
}

type AnalyticsLevel = 'none' | 'essential' | 'performance' | 'all';

class AnalyticsService {
  private static instance: AnalyticsService;
  private enabled: boolean = true;
  private analyticsLevel: AnalyticsLevel = 'essential';
  private eventQueue: AnalyticsEvent[] = [];
  private processingQueue: boolean = false;
  private flushInterval: number | null = null;
  private readonly FLUSH_INTERVAL = 10000;

  private constructor() {
    this.enabled = import.meta.env.VITE_ENABLE_ANALYTICS !== 'false';
    this.analyticsLevel = (import.meta.env.VITE_ANALYTICS_LEVEL || 'essential') as AnalyticsLevel;
    this.startQueueProcessing();
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public enableAnalytics(): void {
    this.enabled = true;
  }

  public disableAnalytics(): void {
    this.enabled = false;
  }

  public isAnalyticsEnabled(): boolean {
    return this.enabled;
  }

  public setAnalyticsLevel(level: string): void {
    if (['none', 'essential', 'performance', 'all'].includes(level)) {
      this.analyticsLevel = level as AnalyticsLevel;
    } else {
      logger.warn('Invalid analytics level, using default', {
        category: 'Analytics',
        data: { providedLevel: level, defaultLevel: this.analyticsLevel }
      });
    }
  }

  public getAnalyticsLevel(): AnalyticsLevel {
    return this.analyticsLevel;
  }

  trackSectionView(sectionId: string, sectionType: string, viewType: string, data?: Record<string, any>) {
    this.trackEvent({
      event_type: 'page_view',
      event_name: 'section_view',
      event_data: { section_id: sectionId, section_type: sectionType, view_type: viewType, ...data }
    });
  }

  trackItemAction(sectionId: string, itemId: string, action: string, itemType: string, data?: Record<string, any>) {
    this.trackEvent({
      event_type: 'interaction',
      event_name: 'item_action',
      event_data: { section_id: sectionId, item_id: itemId, action, item_type: itemType, ...data }
    });
  }

  private shouldTrackEvent(eventType: string): boolean {
    if (!this.enabled) return false;
    switch (this.analyticsLevel) {
      case 'none': return false;
      case 'essential': return ['error', 'navigation', 'user'].includes(eventType);
      case 'performance': return ['error', 'navigation', 'user', 'performance'].includes(eventType);
      case 'all': return true;
      default: return false;
    }
  }

  private startQueueProcessing(): void {
    if (this.flushInterval !== null) {
      clearInterval(this.flushInterval);
    }
    this.flushInterval = window.setInterval(() => { this.processQueue(); }, this.FLUSH_INTERVAL);
    window.addEventListener('beforeunload', () => { this.processQueue(true); });
  }

  private async processQueue(isFinal: boolean = false): Promise<void> {
    if (this.processingQueue || this.eventQueue.length === 0) return;

    try {
      this.processingQueue = true;
      const events = [...this.eventQueue];
      this.eventQueue = [];

      const batchSize = 10;
      for (let i = 0; i < events.length; i += batchSize) {
        const batch = events.slice(i, i + batchSize);
        await this.sendEvents(batch);
      }
    } catch (error) {
      logger.error('Error processing analytics event queue', {
        category: 'Analytics',
        data: { error }
      });
      if (!isFinal) {
        this.eventQueue = [...this.eventQueue];
      }
    } finally {
      this.processingQueue = false;
    }
  }

  private async sendEvents(events: AnalyticsEvent[]): Promise<void> {
    if (events.length === 0) return;

    try {
      const sessionId = sessionTracker.getSessionId();
      if (!sessionId) throw new Error('Session ID not available');

      const eventsToInsert = events.map(event => ({
        id: uuidv4(),
        session_id: sessionId,
        hotel_id: HOTEL_ID,
        anonymous_id: sessionTracker.getAnonymousId(),
        event_type: event.event_type,
        event_name: event.event_name,
        event_data: event.event_data || {},
        page_path: event.page_path || window.location.pathname,
        created_at: new Date().toISOString()
      }));

      const { error } = await supabaseClient
        .from('analytics_events')
        .insert(eventsToInsert);

      if (error) throw error;
    } catch (error) {
      logger.error('Error sending analytics events', {
        category: 'Analytics',
        data: { error, events: events.map(e => ({ type: e.event_type, name: e.event_name })) }
      });
      throw error;
    }
  }

  public trackEvent(event: AnalyticsEvent): void {
    if (!this.shouldTrackEvent(event.event_type)) return;
    this.eventQueue.push(event);
    if (this.eventQueue.length >= 20) {
      this.processQueue();
    }
  }

  public trackPageView(path: string = window.location.pathname): void {
    this.trackEvent({
      event_type: 'navigation',
      event_name: 'page_view',
      event_data: { path, title: document.title, referrer: document.referrer },
      page_path: path
    });
  }

  public trackError(error: Error, context?: Record<string, any>): void {
    this.trackEvent({
      event_type: 'error',
      event_name: error.name,
      event_data: { message: error.message, stack: error.stack, context }
    });
  }
}

export const analyticsService = AnalyticsService.getInstance();
